import { useMemo, useState } from 'react';
import { useUserStore } from '@core/user/useUserStore';
import { UserRole } from '@core/generated/gql-types';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import Button from '@/components/Button';
import useFeatureView from '@/hooks/useFeatureView';
import { getClientUsersView } from '@/modules/clientUsers/views/clientUsers.view';
import { useClientUsersData } from '@/modules/clientUsers/hooks/useClientUsersData';
import InviteUserModal from '@/modules/clientUsers/components/InviteUserModal';
import {
  combineAndSortAssignmentOptions,
  getUserDisplayName,
  SiteAssignmentOption,
} from '@/hooks/useSiteAssignment';
import ClientRolesAndPermissionsDetails from '@/modules/clients/components/ClientRolesAndPermissionsDetails';

interface ClientUsersProps {
  /**
   * Client whose users to show. Provided by the tenant portal (client detail
   * tab). When omitted (client portal), it falls back to the current user's
   * client.
   */
  clientId?: string;
}

const ClientUsers: React.FC<ClientUsersProps> = ({ clientId }) => {
  const { getTerm } = useTemplateStore();
  const { currentUser } = useUserStore();
  const resolvedClientId = clientId ?? currentUser?.clientId ?? undefined;

  const view = useFeatureView(getClientUsersView);
  const {
    users,
    invites,
    isLoading,
    refetchInvites,
    revokeInvite,
    totalClientSites,
  } = useClientUsersData(resolvedClientId);

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [revokingInviteId, setRevokingInviteId] = useState<string | null>(null);
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null);

  const getRowKey = (row: SiteAssignmentOption) => `${row.type}-${row.id}`;

  // Total sites for the client. Client Admins automatically get access to every
  // site, so this is used as their assigned-site count.

  // Merge accepted users and pending invites into a single list sorted by email.
  const combinedRows = useMemo(
    () => combineAndSortAssignmentOptions(users, invites),
    [users, invites],
  );

  const selectedRow = useMemo(
    () => combinedRows.find((row) => getRowKey(row) === selectedRowKey) ?? null,
    [combinedRows, selectedRowKey],
  );

  const handleInviteSuccess = () => {
    refetchInvites();
  };

  const handleRevokeInvite = async (inviteId: string) => {
    setRevokingInviteId(inviteId);
    try {
      await revokeInvite(inviteId);
    } catch (error) {
      console.error('Error revoking invite:', error);
    } finally {
      setRevokingInviteId(null);
    }
  };

  if (selectedRow && resolvedClientId) {
    return (
      <div className="h-full w-full rounded-lg bg-white p-6 shadow-sm">
        <ClientRolesAndPermissionsDetails
          row={selectedRow}
          clientId={resolvedClientId}
          onBack={() => setSelectedRowKey(null)}
        />
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Users</h2>
        {view.actions.showInvite && (
          <Button
            variant="primary"
            onClick={() => setIsInviteModalOpen(true)}
            className="text-sm"
          >
            Invite New User
          </Button>
        )}
      </div>
      <div className="space-y-4">
        <p className="mb-4 text-gray-600">
          Manage {getTerm(TemplateTerm.CLIENT, false)} users here.
        </p>

        {isLoading ? (
          <div className="animate-pulse">
            <div className="mb-4 h-8 w-full rounded-sm bg-gray-200"></div>
            <div className="space-y-3">
              <div className="h-4 w-3/4 rounded-sm bg-gray-200"></div>
              <div className="h-4 w-1/2 rounded-sm bg-gray-200"></div>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                  >
                    Email
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase"
                  >
                    Sites
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs font-medium tracking-wider text-gray-500 uppercase"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {combinedRows.map((row) => {
                  if (row.type === 'invite') {
                    const { invite } = row;
                    const siteCount =
                      invite.userRole === UserRole.ClientAdmin
                        ? totalClientSites
                        : (invite.siteIds?.length ?? 0);

                    return (
                      <tr
                        key={`invite-${invite.id}`}
                        onClick={() => setSelectedRowKey(getRowKey(row))}
                        className="cursor-pointer hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                              Invited
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {invite.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {invite.userRole}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {siteCount}
                          </div>
                        </td>
                        <td
                          onClick={(event) => event.stopPropagation()} // Prevent the Revoke button click from triggering the row click.
                          className="flex items-center justify-center px-6 py-4 whitespace-nowrap"
                        >
                          <Button
                            variant="secondary"
                            onClick={() => handleRevokeInvite(invite.id)}
                            disabled={revokingInviteId === invite.id}
                            className="!bg-red-600 !px-3 !py-1.5 text-sm hover:!bg-red-700"
                          >
                            {revokingInviteId === invite.id
                              ? 'Revoking...'
                              : 'Revoke'}
                          </Button>
                        </td>
                      </tr>
                    );
                  }

                  const { user } = row;
                  const siteCount =
                    user.role === UserRole.ClientAdmin
                      ? totalClientSites
                      : (user.siteIds?.length ?? 0);
                  const displayName = getUserDisplayName(user);

                  return (
                    <tr
                      key={`user-${user.id}`}
                      onClick={() => setSelectedRowKey(getRowKey(row))}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {displayName ? (
                            <div className="flex flex-col items-start gap-1">
                              <span className="text-sm font-medium text-gray-900">
                                {displayName}
                              </span>
                            </div>
                          ) : (
                            <span className="mt-0.5 inline-flex w-fit items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                              Invited
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {user.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{user.role}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{siteCount}</div>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        {/* No actions for accepted users */}
                      </td>
                    </tr>
                  );
                })}
                {combinedRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No users found for this{' '}
                      {getTerm(TemplateTerm.CLIENT, false)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resolvedClientId && (
        <InviteUserModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          clientId={resolvedClientId}
          onSuccess={handleInviteSuccess}
        />
      )}
    </div>
  );
};

export default ClientUsers;
